import type { Annotation } from "@/types/annotation";
import { Prediction } from "@/utils/api/inference";

export const DEFAULT_CONFIDENCE_THRESHOLD = 0.5;

export const predictionToAnnotation = (prediction: Prediction, frameId: string): Annotation => {
    const {
        x,
        y,
        width,
        height,
        detection_id: id,
        class: classLabel,
        thumbnailDataUrl,
        fileId,
        confidence
    } = prediction;
    return {
        id,
        x,
        y,
        width,
        height,
        label: classLabel.replace(/\s*\(\d+%\)$/, ""),
        draft: false,
        thumbnailDataUrl,
        fileId,
        frameId,
        confidence
    }; // remove confidence from label
};

/**
 * OwlVIT returns a confidence score between 0 and 1, but the model is super sensitive and it usually works great
 * within 0.95 +- 0.02 - so we have this heuristic to adjust the confidence score to a score between 0 and 100.
 * @param confidence - The confidence score from OwlVIT
 * @returns The adjusted confidence score
 */
const adjustedConfidenceFromOwlvit = (confidence: number) => {
    // Less than 0.75 the model gives super non-meaningful results
    // So we adjust the confidence score to a score between 0 and 100
    return (confidence - 0.75) / 0.25;
};

/**
 * We want to adjust the confidence score to a score between 0 and 1
 * @param adjustedConfidence - The adjusted confidence score
 * @returns The confidence score from OwlVIT
 */
const adjustedConfidenceToOwlvit = (adjustedConfidence: number) => {
    // We want to adjust the confidence score to a score between 0 and 1
    return adjustedConfidence * 0.25 + 0.75;
};

export { adjustedConfidenceFromOwlvit, adjustedConfidenceToOwlvit };
